// ABOUTME: Value for an always-editable input that has no separate read mode.
// ABOUTME: Shows the server translation until the user types, their text after (WA-762).

import { useState } from "react"
import type { L10nFragment } from "@/graphql/main/gql/graphql"
import { resolveL10n } from "./fn"

/**
 * Inline editors (the workout constructor) have no view/edit toggle, so the
 * input itself is what the user reads. It therefore shows the translation —
 * and the moment the user types, it shows their own text instead: the stored
 * value has just become theirs, while the `l10n` twin still describes the
 * previous text and would otherwise snap the field back to the old wording.
 *
 * Until the first keystroke the stored value is left untouched, so merely
 * opening a workout never rewrites anyone's step names.
 */
export function useTranslatedDraft(l10n: L10nFragment | null | undefined, value: string) {
  // What the user last typed; null until the first keystroke.
  const [typed, setTyped] = useState<string | null>(null)
  // What `value` was on the previous render. Both are needed: a keystroke
  // renders once before the editor's own state write comes back (twice, if that
  // write is debounced), and during those renders `value` is still the old text
  // — dropping the draft there would snap the field back mid-typing.
  const [prevValue, setPrevValue] = useState(value)

  // `value` changed, and not to what the user typed: the text was replaced from
  // the outside (a refetch, an undo, or the card being reused for another step),
  // so the draft belonged to the text that just went away.
  if (value !== prevValue) {
    setPrevValue(value)
    if (typed !== null && value !== typed) {
      setTyped(null)
    }
  }

  const onTyped = (next: string) => {
    setTyped(next)
  }

  return {
    value: typed !== null ? value : resolveL10n(l10n, value).display,
    onTyped,
  }
}
